import React, { useCallback, useMemo } from 'react';
import Plot from 'react-plotly.js';
import type { PlotMouseEvent } from 'plotly.js';
import type { SampleRow } from '@/types/sample';
import {
  buildTrainingTrace,
  buildGeneratedTrace,
  buildTargetTrace,
  buildBestSampleTrace,
  buildChartLayout,
  getSelectedPointIndices,
} from './plotHelpers';

export interface ScatterChartProps {
  /** Column key for X axis (e.g. "Cz"). */
  xCol: string;
  /** Column key for Y axis (e.g. "Torsion"). */
  yCol: string;
  /** X axis display label. */
  xLabel: string;
  /** Y axis display label. */
  yLabel: string;
  /** Training/original data rows. */
  trainingData: SampleRow[];
  /** Generated (new) sample rows. */
  generatedData: SampleRow[];
  /** Currently selected sample ID (or null). */
  selectedId: string | null;
  /** Target point X value (or null if not set). */
  targetX: number | null;
  /** Target point Y value (or null if not set). */
  targetY: number | null;
  /** Best sample X value (or null). */
  bestX: number | null;
  /** Best sample Y value (or null). */
  bestY: number | null;
  /** Best sample ID. */
  bestId: string | null;
  /** Callback when a sample point is clicked. */
  onSelect: (id: string | null) => void;
}

const ScatterChart: React.FC<ScatterChartProps> = ({
  xCol,
  yCol,
  xLabel,
  yLabel,
  trainingData,
  generatedData,
  selectedId,
  targetX,
  targetY,
  bestX,
  bestY,
  bestId,
  onSelect,
}) => {
  const trainingTrace = useMemo(
    () => buildTrainingTrace(trainingData, xCol, yCol),
    [trainingData, xCol, yCol],
  );

  const generatedTrace = useMemo(
    () => buildGeneratedTrace(generatedData, xCol, yCol),
    [generatedData, xCol, yCol],
  );

  const targetTrace = useMemo(
    () => buildTargetTrace(targetX, targetY, xCol, yCol),
    [targetX, targetY, xCol, yCol],
  );

  const bestTrace = useMemo(
    () => buildBestSampleTrace(bestX, bestY, bestId, xCol, yCol),
    [bestX, bestY, bestId, xCol, yCol],
  );

  const selectedPoints = useMemo(
    () => getSelectedPointIndices(generatedData, selectedId),
    [generatedData, selectedId],
  );

  const layout = useMemo(() => buildChartLayout(xLabel, yLabel), [xLabel, yLabel]);

  // Apply selection to the generated trace
  const tracesWithSelection = useMemo(() => {
    const genWithSelection = {
      ...generatedTrace,
      selectedpoints: selectedPoints.length > 0 ? selectedPoints : undefined,
    };
    return [trainingTrace, genWithSelection, targetTrace, bestTrace];
  }, [trainingTrace, generatedTrace, targetTrace, bestTrace, selectedPoints]);

  const handleClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => {
      const point = event.points[0];
      if (!point) return;
      // Only handle clicks on the generated trace (trace index 1)
      if (point.curveNumber !== 1) return;
      const customdata = point.customdata as string | undefined;
      if (customdata) {
        onSelect(customdata);
      }
    },
    [onSelect],
  );

  const handleDoubleClick = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const config = useMemo(
    () => ({
      displayModeBar: true,
      displaylogo: false,
      responsive: true,
      scrollZoom: true,
      doubleClick: 'reset+autosize' as const,
      modeBarButtonsToRemove: ['select2d', 'lasso2d'] as import('plotly.js').ModeBarDefaultButtons[],
    }),
    [],
  );

  return (
    <Plot
      data={tracesWithSelection as Plotly.Data[]}
      layout={layout}
      config={config}
      useResizeHandler
      style={{ width: '100%', height: '100%' }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    />
  );
};

export default React.memo(ScatterChart);
